import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { authClient } from '@/lib/auth-client';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { useUser } from '@/context/user-provider';
import { toast } from 'sonner';

export const DeviceApprovalPage = () => {
  const [searchParams] = useSearchParams();
  const userCode = searchParams.get('user_code'); // Note: parameter name from RFC 8628
  const [isProcessing, setIsProcessing] = useState(false);
  const [deviceInfo, setDeviceInfo] = useState<any>(null);
  const [error, setError] = useState<string | null>(null);
  const navigate = useNavigate();
  const { user, isLoading } = useUser();

  // Check if user is already authenticated
  useEffect(() => {
    if (!isLoading && !user) {
      // Redirect to login with return URL to this page
      const returnTo = window.location.pathname + window.location.search;
      navigate(`/login?returnTo=${encodeURIComponent(returnTo)}`);
    }
  }, [user, isLoading, navigate]);

  // Fetch device info when component mounts
  useEffect(() => {
    if (!userCode || !user) return;

    const fetchDeviceCode = async () => {
      try {
        const response = await authClient.device({
          query: { user_code: userCode },
        });
        
        if (response.data) {
          setDeviceInfo(response.data);
        } else {
          setError('Invalid or expired code');
        }
      } catch (err: any) {
        console.error('Error fetching device info:', err);
        setError(err?.message || 'Failed to fetch device information');
      }
    };

    fetchDeviceCode();
  }, [userCode, user]);

  const handleApprove = async () => {
    if (!userCode) return;

    setIsProcessing(true);
    try {
      await authClient.device.approve({
        userCode: userCode,
      });
      toast.success('Device authorized successfully!');
      navigate('/');
    } catch (error: any) {
      console.error('Device approval error:', error);
      toast.error(error?.message || 'Failed to approve device');
    } finally {
      setIsProcessing(false);
    }
  };

  const handleDeny = async () => {
    if (!userCode) return;

    setIsProcessing(true);
    try {
      await authClient.device.deny({
        userCode: userCode,
      });
      toast.success('Device authorization denied');
      navigate('/');
    } catch (error: any) {
      console.error('Device denial error:', error);
      toast.error(error?.message || 'Failed to deny device');
    } finally {
      setIsProcessing(false);
    }
  };

  // If user is still loading, show loading state
  if (isLoading) {
    return (
      <div className="flex items-center justify-center min-h-screen">
        <div className="text-lg">Loading...</div>
      </div>
    );
  }

  // If user is not authenticated, don't render anything (they'll be redirected by useEffect)
  if (!user) {
    return null;
  }

  if (error) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-muted/40 p-4">
        <Card className="w-full max-w-md">
          <CardHeader>
            <CardTitle className="text-xl">Device Authorization Error</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="text-center text-destructive">
              {error}
            </div>
            <Button 
              className="w-full mt-4"
              onClick={() => navigate('/')}
            >
              Go Home
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen flex items-center justify-center bg-muted/40 p-4">
      <Card className="w-full max-w-md">
        <CardHeader>
          <CardTitle className="text-xl">Device Authorization Request</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="space-y-4">
            <div className="text-center">
              <div className="text-sm text-muted-foreground mb-2">
                A device is requesting access to your account
              </div>
              <div className="text-lg font-mono bg-muted p-3 rounded-md">
                {userCode}
              </div>
              {deviceInfo?.clientId && (
                <div className="mt-2 text-sm">
                  <span className="text-muted-foreground">Client: </span>
                  <span>{deviceInfo.clientId}</span>
                </div>
              )}
              {deviceInfo?.scope && (
                <div className="mt-1 text-sm">
                  <span className="text-muted-foreground">Scope: </span>
                  <span>{deviceInfo.scope}</span>
                </div>
              )}
            </div>

            <div className="flex flex-col sm:flex-row gap-2 pt-4">
              <Button 
                variant="outline" 
                onClick={handleDeny}
                disabled={isProcessing}
                className="flex-1"
              >
                {isProcessing ? 'Processing...' : 'Deny'}
              </Button>
              <Button 
                onClick={handleApprove}
                disabled={isProcessing}
                className="flex-1"
              >
                {isProcessing ? 'Processing...' : 'Approve'}
              </Button>
            </div>
          </div>
        </CardContent>
      </Card>
    </div>
  );
};